import React, { useState, useEffect, useCallback } from "react";
import s from "../Description/mainDescriotion.module.scss";
import circle from "../../Images/circle.svg";
import circleUncheck from "../../Images/circleUncheck.svg";
import useWindowSize from "pages/CreateEvent/HookSize";
import { FormDataType } from "../../../../types/types";
import plus from "../../Images/plus.png";
import minus from "../../Images/minus.png";
import { isValidRussianPhone, formatRussianPhoneInput } from "utils/phones";

interface MainDescriptionProps {
  formData: FormDataType;
  editableSocialId: (number | undefined)[] | undefined;
  editableReviewId: (number | undefined) | undefined;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  hasError: boolean;
}

interface EmailField {
  email: string;
}

export const MainDescription: React.FC<MainDescriptionProps> = ({
  formData,
  editableSocialId,
  editableReviewId,
  setFormData,
  hasError,
}) => {
  const initialMediaLinks = formData?.social_media_links?.map((link) => ({
    email: link.url,
  })) || [{ email: "" }];

  const initialHideAddress = formData?.hide_address || false;

  const [emailFields, setEmailFields] =
    useState<EmailField[]>(initialMediaLinks);
  const [checked, setChecked] = useState(initialHideAddress);
  const [width] = useWindowSize();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const normalizeUrl = useCallback((url: string): string => {
    if (!url) return url;
    const hasProtocol = /^(https?|ftp):\/\//i.test(url);
    if (!hasProtocol) {
      return `https://${url}`;
    }
    return url;
  }, []);

  const validateField = useCallback(
    (name: string, value: string): string => {
      const urlRegex =
        /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;

      const currentDate = formData?.date || "";
      const currentEndDate = formData?.endDate || "";
      const currentTime = formData?.time || "";
      const currentEndTime = formData?.endTime || "";

      switch (name) {
        case "name":
          if (!value.trim()) return "Название события обязательно";
          if (value.length < 3) return "Минимум 3 символа";
          if (value.length > 50) return "Максимум 50 символов";
          return "";

        case "date": {
          if (!value) return "Укажите дату проведения";
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const eventDate = new Date(value);
          if (eventDate < today) return "Дата не может быть в прошлом";
          return "";
        }

        case "time": {
          if (!value) return "Укажите время проведения";
          if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value))
            return "Некорректное время";

          if (currentDate) {
            const [hours, minutes] = value.split(":").map(Number);
            const eventDateTime = new Date(currentDate);
            eventDateTime.setHours(hours, minutes, 0, 0);

            const now = new Date();
            const timeDifference = eventDateTime.getTime() - now.getTime();

            if (timeDifference < 3600000) {
              return "Время начала события должно быть как минимум через 1 час от текущего момента";
            }
          }
          return "";
        }

        case "endDate": {
          if (!value) return "Укажите дату окончания";
          if (currentDate && new Date(value) < new Date(currentDate))
            return "Дата окончания не может быть раньше даты начала";

          if (
            currentDate &&
            value === currentDate &&
            currentTime &&
            currentEndTime
          ) {
            const start = new Date(`${currentDate}T${currentTime}`);
            const end = new Date(`${value}T${currentEndTime}`);
            if (end <= start) {
              return "Время окончания должно быть позже времени начала";
            }
          }
          return "";
        }

        case "endTime": {
          if (!value) return "Укажите время окончания";
          if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value))
            return "Некорректное время";

          if (currentDate && currentEndDate && currentTime) {
            const startDateTime = new Date(`${currentDate}T${currentTime}`);
            const endDateTime = new Date(`${currentEndDate}T${value}`);

            if (endDateTime <= startDateTime) {
              return "Время окончания должно быть позже времени начала";
            }
          }
          return "";
        }

        case "place.name":
          if (!value.trim()) return "Укажите место проведения";
          if (value.length < 2) return "Минимум 2 символа";
          if (value.length > 50) return "Максимум 50 символов";
          return "";

        case "place.city":
          if (!value.trim()) return "Укажите город";
          if (value.length < 2) return "Минимум 2 символа";
          if (value.length > 50) return "Максимум 50 символов";
          return "";

        case "place.address":
          if (!value.trim()) return "Укажите адрес";
          if (value.length < 2) return "Минимум 2 символа";
          if (value.length > 50) return "Максимум 50 символов";
          if (!/\d/.test(value))
            return "Адрес должен содержать цифры (например: улица 12)";
          return "";

        case "phone":
          if (!value.trim()) return "Укажите телефон";
          if (value && !isValidRussianPhone(value))
            return "Формат: +7 XXX XXX XX XX";
          return "";

        case "description":
          if (!value.trim()) return "Заполните описание";
          if (value && value.length < 2) return "Минимум 2 символа";
          if (value && value.length > 2000) return "Максимум 2000 символов";
          return "";

        case "review.url":
          if (value && !urlRegex.test(normalizeUrl(value)))
            return "Некорректный URL";
          return "";

        case "video":
          if (value && !urlRegex.test(normalizeUrl(value)))
            return "Некорректный URL видео";
          return "";

        default:
          return "";
      }
    },
    [
      formData?.date,
      formData?.endDate,
      formData?.time,
      formData?.endTime,
      normalizeUrl,
    ]
  );

  const handleInputChange = (name: string, value: string) => {
    const error = validateField(name, value);

    if (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev: any) => {
      const keys = name.split(".");
      if (keys.length === 1) {
        return {
          ...prev,
          [name]: value,
        };
      }

      const newData = { ...prev };
      let current: any = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextRaw = e.target.value;
    const prevFormatted = formData?.phone || "";

    const formatted = formatRussianPhoneInput(nextRaw, prevFormatted);

    handleInputChange("phone", formatted);
  };

  const handleEmailChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const newEmailFields = emailFields.map((field, i) =>
      i === index ? { ...field, email: value } : field
    );
    setEmailFields(newEmailFields);

    const normalizedLinks = newEmailFields.map((field) => ({
      url: normalizeUrl(field.email),
    }));

    setFormData((prev: any) => ({
      ...prev,
      social_media_links: normalizedLinks.map((link, i) => ({
        id: editableSocialId?.[i],
        url: link.url,
      })),
    }));

    const normalizedValue = normalizeUrl(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`email${index}`]: validateField("review.url", normalizedValue),
    }));
  };

  const handlePlaceChange = (
    field: "name" | "city" | "address",
    value: string
  ) => {
    const fullName = `place.${field}`;
    const error = validateField(fullName, value);

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fullName] = error;
      } else {
        delete newErrors[fullName];
      }
      return newErrors;
    });

    setFormData((prev: any) => ({
      ...prev,
      place: {
        ...prev.place,
        [field]: value,
      },
    }));
  };

  const handleReviewChange = (field: "url", value: string) => {
    const normalizedValue = normalizeUrl(value);

    setFormData((prev: any) => ({
      ...prev,
      review: {
        ...prev.review,
        id: editableReviewId,
        url: normalizedValue,
      },
    }));

    handleInputChange(`review.${field}`, normalizedValue);
  };

  const handleCheckboxChange = () => {
    setChecked((prev) => !prev);
    setFormData((prev: any) => ({
      ...prev,
      hide_address: !prev.hide_address,
    }));
  };

  const addEmailField = () => {
    if (emailFields.length < 3) {
      const newEmailFields = [...emailFields, { email: "" }];
      setEmailFields(newEmailFields);
      setFormData((prev: any) => ({
        ...prev,
        social_media_links: newEmailFields.map((field) => ({
          url: field.email,
        })),
      }));
    }
  };

  const removeEmailField = (index: number) => {
    if (emailFields.length > 1) {
      const newEmailFields = emailFields.filter((_, i) => i !== index);
      setEmailFields(newEmailFields);

      setFormData((prev: any) => ({
        ...prev,
        social_media_links: newEmailFields.map((field, i) => ({
          id: editableSocialId?.[i],
          url: field.email,
        })),
      }));

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`email${index}`];

        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith("email")) {
            const num = parseInt(key.replace("email", ""));
            if (num > index) {
              newErrors[`email${num - 1}`] = newErrors[key];
              delete newErrors[key];
            }
          }
        });
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (!hasError) return;

    const newErrors: Record<string, string> = {};

    const register = (fieldName: string, value: string | undefined) => {
      const err = validateField(fieldName, value ?? "");
      if (err) newErrors[fieldName] = err;
    };

    register("name", formData.name);
    register("date", formData.date);
    register("time", formData.time);
    register("endDate", formData.endDate);
    register("endTime", formData.endTime);
    register("place.name", formData.place?.name);
    register("place.city", formData.place?.city);
    register("place.address", formData.place?.address);
    register("phone", formData.phone);
    register("description", formData.description);

    // соцсети
    emailFields.forEach((field, index) => {
      if (field.email) {
        const normalized = normalizeUrl(field.email);
        const err = validateField("review.url", normalized);
        if (err) newErrors[`email${index}`] = err;
      }
    });

    // отзывы
    if (formData.review?.url) {
      const err = validateField("review.url", formData.review.url);
      if (err) newErrors["review.url"] = err;
    }

    setErrors(newErrors);
  }, [hasError, formData, emailFields, validateField, normalizeUrl]);

  const editableDescriptionHeight = formData ? "136px" : "auto";

  return (
    <div className={s.inputSection}>
      {/* Название */}
      <div className={s.mainNameText}>
        <label className={s.inputLabel}>Введите название*</label>
        <input
          maxLength={51}
          className={`${s.mainInput} ${errors.name ? s.errorBorder : ""}`}
          type="text"
          placeholder="Название события"
          value={formData?.name || ""}
          style={{ color: "#262429" }}
          onChange={(e) => {
            handleInputChange("name", e.target.value);
          }}
        />
      </div>
      {errors.name && (
        <div className={s.error} style={{ marginTop: "-25px" }}>
          {errors.name}
        </div>
      )}

      {/* Дата начала */}
      <div className={s.DataText}>
        <label className={s.DataLabel}>Выберите дату*</label>
        <input
          className={`${s.inputData} ${errors.date ? s.errorBorder : ""}`}
          type="date"
          value={formData?.date || ""}
          onChange={(e) => {
            handleInputChange("date", e.target.value);
          }}
        />
      </div>
      {errors.date && <div className={s.error}>{errors.date}</div>}

      {/* Время начала */}
      <div className={s.TimeText}>
        <label className={s.inputLabel}>Выберите время*</label>
        <input
          className={`${s.inputField} ${errors.time ? s.errorBorder : ""}`}
          type="time"
          value={formData?.time || ""}
          style={{ color: "#262429" }}
          onChange={(e) => {
            handleInputChange("time", e.target.value);
          }}
        />
      </div>
      {errors.time && <div className={s.error}>{errors.time}</div>}

      {/* Дата окончания */}
      <div className={s.DataText}>
        <label className={s.DataLabel}>Дата окончания*</label>
        <input
          className={`${s.inputData} ${errors.endDate ? s.errorBorder : ""}`}
          type="date"
          value={formData?.endDate || ""}
          onChange={(e) => {
            handleInputChange("endDate", e.target.value);
          }}
        />
      </div>
      {errors.endDate && <div className={s.error}>{errors.endDate}</div>}

      {/* Время окончания */}
      <div className={s.TimeText}>
        <label className={s.inputLabel}>Время окончания*</label>
        <input
          className={`${s.inputField} ${errors.endTime ? s.errorBorder : ""}`}
          type="time"
          value={formData?.endTime || ""}
          step="60"
          onChange={(e) => {
            handleInputChange("endTime", e.target.value);
          }}
        />
      </div>
      {errors.endTime && <div className={s.error}>{errors.endTime}</div>}

      {/* Локация */}
      <div className={s.LocationText}>
        <div className={s.location}>
          <label className={s.inputLabel}>Место проведения*</label>
          <input
            maxLength={51}
            className={`${s.inputField} ${
              errors["place.name"] ? s.errorBorder : ""
            }`}
            type="text"
            style={{ color: "#262429" }}
            placeholder="Глав Клаб"
            value={formData?.place?.name || ""}
            onChange={(e) => handlePlaceChange("name", e.target.value)}
          />
        </div>
        {errors["place.name"] && (
          <div className={s.error}>{errors["place.name"]}</div>
        )}

        <div className={s.City}>
          <label className={s.inputLabel}>Город*</label>
          <input
            maxLength={51}
            className={`${s.inputField} ${
              errors["place.city"] ? s.errorBorder : ""
            }`}
            type="text"
            style={{ color: "#262429" }}
            placeholder="г.Москва"
            value={formData?.place?.city || ""}
            onChange={(e) => handlePlaceChange("city", e.target.value)}
          />
        </div>
        {errors["place.city"] && (
          <div className={s.error}>{errors["place.city"]}</div>
        )}

        <div className={s.Street}>
          <label className={s.inputLabel}>Адрес*</label>
          <input
            maxLength={51}
            className={`${s.inputField} ${
              errors["place.address"] ? s.errorBorder : ""
            }`}
            type="text"
            style={{ color: "black" }}
            placeholder="Ленина, 59 с4"
            value={formData?.place?.address || ""}
            onChange={(e) => handlePlaceChange("address", e.target.value)}
          />
        </div>
        {errors["place.address"] && (
          <div className={s.error}>{errors["place.address"]}</div>
        )}

        <div className={s.checkBox} onClick={handleCheckboxChange}>
          {checked ? (
            <img src={circleUncheck} alt="Unchecked" />
          ) : (
            <img src={circle} alt="Checked" />
          )}
          Скрыть адрес до покупки
        </div>

        {/* Email */}
        {/* <div className={s.Email}>
          <label className={s.inputLabel}>
            {width <= 768 ? "Контактная почта" : "Email"}
          </label>
          <input
            className={`${s.inputField} ${errors.email ? s.errorBorder : ""}`}
            type="email"
            placeholder="ipvanino12091@gmail.com"
            style={{ color: "#262429" }}
            value={formData?.email || ""}
            onChange={(e) => {
              handleInputChange("email", e.target.value);
            }}
          />
        </div>
        {errors.email && <div className={s.error}>{errors.email}</div>} */}

        {/* Телефон */}
        <div className={s.Tel}>
          <label className={s.inputLabel}>
            {width <= 768 ? "Контактный телефон*" : "Телефон*"}
          </label>
          <input
            className={`${s.inputField} ${errors.phone ? s.errorBorder : ""}`}
            type="tel"
            maxLength={18}
            placeholder="+ 7 (958) - 589–08–87"
            value={formData?.phone || ""}
            style={{ color: "#262429" }}
            onChange={handlePhoneChange}
          />
        </div>
        {errors.phone && <div className={s.error}>{errors.phone}</div>}
      </div>

      {/* Описание */}
      <textarea
        className={`${s.Description} ${
          errors.description ? s.errorBorder : ""
        }`}
        style={{ color: "#262429", height: editableDescriptionHeight }}
        placeholder="Описание мероприятия*"
        value={formData?.description || ""}
        onChange={(e) => {
          handleInputChange("description", e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
      />
      {errors.description && (
        <div className={s.error} style={{ marginTop: "-25px" }}>
          {errors.description}
        </div>
      )}

      {/* Соцсети */}
      <div className={s.Social}>
        <label className={s.inputLabel}>Ссылка на соц. сеть</label>
        <div className={s.socialContainer}>
          {emailFields.map((field, index) => (
            <div className={s.socialContainer_input} key={index}>
              <div>
                <input
                  className={`${s.inputField} ${
                    errors[`email${index}`] ? s.errorBorder : ""
                  }`}
                  type="url"
                  value={field.email}
                  style={{ color: "#262429" }}
                  onChange={(event) => handleEmailChange(index, event)}
                  placeholder="https://www.instagram.com/vkfest"
                />
                {errors[`email${index}`] && (
                  <div className={s.error} style={{ marginTop: "0px" }}>
                    {errors[`email${index}`]}
                  </div>
                )}
              </div>
              {emailFields.length > 1 && (
                <button
                  type="button"
                  className={s.delButton}
                  style={{ marginLeft: "20px" }}
                  onClick={() => removeEmailField(index)}
                >
                  <img src={minus} alt="minus" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          className={`${s.addButton} ${
            emailFields.length >= 3 ? s.disabledButton : ""
          }`}
          onClick={addEmailField}
        >
          Добавить соц. сеть <img src={plus} alt="plus" />
        </button>
      </div>

      {/* Ссылка на отзывы */}
      <div className={s.LinkToTheReview}>
        <label className={s.inputLabel}>Ссылка на отзывы</label>
        <input
          className={`${s.inputField} ${
            errors["review.url"] ? s.errorBorder : ""
          }`}
          type="url"
          style={{ color: "#262429" }}
          placeholder="https://www.instagram.com/vkfest/feedback"
          value={formData?.review?.url || ""}
          onChange={(e) => {
            handleReviewChange("url", e.target.value);
          }}
        />
      </div>
      {errors["review.url"] && (
        <div className={s.error} style={{ marginTop: "-25px" }}>
          {errors["review.url"]}
        </div>
      )}
    </div>
  );
};
